<template>
    <div
        v-if="tonalThemeEnabled"
        v-bind="$attrs"
        class="event tonal-event relative flex flex-wrap sm:flex-nowrap items-center min-w-0 p-2.5 rounded-lgr text-left transition-colors"
        :class="{ 'is-active': active, 'has-divider': divider, 'are-actions-visible': actionsAlwaysVisible }">
        <div class="time scaffold-details-small text-ink flex w-10 shrink-0 flex-col mr-5 sm:mr-10">
            <time :datetime="startTime.toISO() ?? undefined">{{ formattedStartTime }}</time>
            <time class="time-end text-ink-muted" :datetime="endTime.toISO() ?? undefined">{{ formattedEndTime }}</time>
        </div>
        <div class="meta flex min-w-0 flex-1 flex-col">
            <div class="event-title body-small text-ink truncate transition-[text-shadow]">{{ title }}</div>
            <div v-if="showDate || !hideAttendees" class="subtitle scaffold-details-small text-ink-muted truncate">
                <span v-if="showDate">{{ formattedDate }}</span>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: list separator -->
                <span v-if="showDate && !hideAttendees"> · </span>
                <span v-if="!hideAttendees">{{ attendeeText }}</span>
            </div>
        </div>
        <div
            v-if="$slots.actions"
            class="actions opacity-0 pointer-events-none basis-full sm:basis-auto ml-auto mt-2 sm:mt-0 flex shrink-0 items-center justify-end sm:justify-start gap-2.5 sm:pl-4">
            <slot name="actions"></slot>
        </div>
    </div>
    <div v-else v-bind="$attrs" class="flex items-start gap-4">
        <div class="flex flex-1">
            <div class="w-1 rounded-lg divider-background"></div>
            <div
                class="event legacy-event flex-1 flex flex-col sm:flex-row sm:items-center px-2.5 rounded-l-none rounded-r-lg gap-2 sm:gap-1 transition-colors"
                :class="{ highlighted }">
                <div class="grow">
                    <div class="body-small text-ink-muted mb-1" :class="hasCoachingSession ? 'font-normal' : 'font-medium'">{{ title }}</div>
                    <div class="flex flex-wrap gap-x-4">
                        <div class="body-small text-ink-muted">
                            <span v-if="showDate" class="mr-3">{{ legacyFormattedDate }}</span>
                            <span class="whitespace-nowrap">{{ legacyTime }}</span>
                        </div>
                        <div v-if="!hideAttendees" class="body-small text-ink-muted">{{ attendeeText }}</div>
                    </div>
                </div>
                <div v-if="$slots.actions" class="flex justify-end">
                    <slot name="actions"></slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { formatDate } from "~vue/utils/formatDate";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { DateTime } from "luxon";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineOptions({ inheritAttrs: false });

type CalendarEventAttendee = {
    name: string;
    email: string;
};

type Props = {
    title: string;
    startTime: DateTime;
    endTime: DateTime;
    attendees?: CalendarEventAttendee[];
    hideAttendees?: boolean;
    showDate?: boolean;
    active?: boolean;
    divider?: boolean;
    actionsAlwaysVisible?: boolean;
    calendarColor?: string;
    highlighted?: boolean;
    hasCoachingSession?: boolean;
    isPast?: boolean;
};

const {
    title,
    startTime,
    endTime,
    attendees = [],
    hideAttendees = false,
    showDate = false,
    active = false,
    divider = false,
    actionsAlwaysVisible = false,
    calendarColor = "#9596ff",
    highlighted = false,
    hasCoachingSession = false,
    isPast = false,
} = defineProps<Props>();

// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.

const attendeeText = computed(() => {
    const count = attendees.length;
    if (count === 0) {
        return t("components.calendar.event.noAttendees");
    }
    if (count <= (tonalThemeEnabled.value ? 2 : 3)) {
        return attendees.map(({ name, email }) => name || email).join(", ");
    }
    if (count >= 10) {
        return t("components.calendar.event.manyAttendees");
    }
    return t("components.calendar.event.attendeeCount", { count }, count);
});

const formattedDate = computed(() => formatDate(startTime, "month-day-long"));
// Deliberately 24h in every language: the tonal time column is w-10 and "12:30 PM" does not fit.
// Switching this surface to the locale's hour cycle (BaseDate format="time") needs a layout pass first.
const formattedStartTime = computed(() => startTime.toFormat("H:mm"));
const formattedEndTime = computed(() => endTime.toFormat("H:mm"));
const legacyFormattedDate = computed(() => formatDate(startTime, "date"));
const legacyTime = computed(() => `${formatDate(startTime, "time")} - ${formatDate(endTime, "time")}`);
const dividerBackground = computed(() => (isPast ? "var(--color-border-strong)" : calendarColor));
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

.tonal-event:hover,
.tonal-event:focus-within,
.tonal-event.is-active {
    @apply bg-surface;
}

.tonal-event.has-divider::after {
    content: "";
    @apply absolute bottom-0 left-0 right-0 h-px bg-border;
}

.tonal-event:hover::after,
.tonal-event:focus-within::after,
.tonal-event.is-active::after {
    @apply hidden;
}

.tonal-event:hover .event-title,
.tonal-event:focus-within .event-title,
.tonal-event.is-active .event-title {
    text-shadow: currentcolor 0.3px 0 0;
}

.tonal-event:hover .actions,
.tonal-event:focus-within .actions,
.tonal-event.is-active .actions,
.tonal-event.are-actions-visible .actions {
    @apply opacity-100 pointer-events-auto;
}

@layer components {
    .legacy-event:hover,
    .legacy-event.highlighted {
        @apply bg-surface-hover;
    }
}

.divider-background {
    background-color: v-bind(dividerBackground);
}
</style>
